/**
 * Temporal smoothing (pipeline stage 5).
 *
 * §6 and §10 are explicit that a single frame must never be interpreted as distraction.
 * This is where that becomes true. A short time-bounded window of pose samples yields:
 *
 * - the **median** pose — a median, not a mean, because a mean is dragged by the one bad
 *   frame that a blink, a hand across the face or a detection glitch produces;
 * - the **scatter** (median absolute deviation) of that window, which is what says whether
 *   the head was steady or mid-movement.
 *
 * Scatter matters as much as the value. A head sweeping from the monitor to a colleague
 * passes through every angle in between, and each of those has a confident-looking median.
 * Reporting scatter lets the state engine decline to judge someone mid-turn.
 */

import { combineConfidence, headPose, type HeadPose } from '../models.js';

export interface SmoothedPose {
  readonly pose: HeadPose;
  readonly sampleCount: number;
  readonly windowSpanSeconds: number;
  readonly meanConfidence: number;
  readonly yawMad: number;
  readonly pitchMad: number;
  /** 1 = perfectly steady, 0 = scatter at or beyond the configured reference. */
  readonly stability: number;
  /** Confidence after accounting for head movement within the window. */
  readonly effectiveConfidence: number;
}

export interface SmoothingOptions {
  windowSeconds: number;
  /**
   * Minimum samples before any smoothed value is offered. Below this the smoother returns
   * null, which the classifier turns into `not_detected` — an honest "we do not know yet"
   * rather than a one-frame guess.
   */
  minSamples: number;
  /**
   * Samples older than this are dropped even when the window is underfull, so a stale pose
   * from before a gap (camera unplugged, laptop asleep) is never averaged with a fresh one.
   */
  maxSampleAgeSeconds: number;
  stabilityReferenceDegrees?: number;
}

interface Sample {
  readonly ts: number;
  readonly pose: HeadPose;
  readonly confidence: number;
}

export class PoseSmoother {
  private readonly windowSeconds: number;
  private readonly minSamples: number;
  private readonly maxAge: number;
  private readonly stabilityReference: number;
  private samples: Sample[] = [];

  constructor(options: SmoothingOptions) {
    if (options.windowSeconds <= 0) throw new RangeError('windowSeconds must be > 0');
    if (options.minSamples < 1) throw new RangeError('minSamples must be >= 1');
    this.windowSeconds = options.windowSeconds;
    this.minSamples = options.minSamples;
    this.maxAge = Math.max(options.maxSampleAgeSeconds, options.windowSeconds);
    this.stabilityReference = Math.max(1e-6, options.stabilityReferenceDegrees ?? 12);
  }

  /** Record a pose sample. Samples must arrive in non-decreasing time order. */
  add(monotonicTs: number, pose: HeadPose, confidence: number): void {
    this.samples.push({
      ts: monotonicTs,
      pose,
      confidence: Math.max(0, Math.min(1, confidence)),
    });
    this.evict(monotonicTs);
  }

  /**
   * Note that this instant produced no pose (no face, bad frame, no camera).
   *
   * Expiring old samples on a gap is the point: a face that disappears must stop
   * contributing a stale "facing forward" reading within one window, rather than lingering
   * because no new samples arrive to push it out.
   */
  markGap(monotonicTs: number): void {
    this.evict(monotonicTs);
  }

  private evict(now: number): void {
    const cutoff = now - this.maxAge;
    if (this.samples.length > 0 && (this.samples[0] as Sample).ts >= cutoff) return;
    this.samples = this.samples.filter((sample) => sample.ts >= cutoff);
  }

  /** Median pose over the window, or null if there is not enough recent data. */
  smoothed(now?: number): SmoothedPose | null {
    if (now !== undefined) this.evict(now);
    if (this.samples.length === 0) return null;

    const last = this.samples[this.samples.length - 1] as Sample;
    const reference = now ?? last.ts;
    const windowStart = reference - this.windowSeconds;
    const inWindow = this.samples.filter((sample) => sample.ts >= windowStart);

    if (inWindow.length < this.minSamples) return null;

    const yaws = inWindow.map((sample) => sample.pose.yaw);
    const pitches = inWindow.map((sample) => sample.pose.pitch);
    const rolls = inWindow.map((sample) => sample.pose.roll);

    const medianYaw = median(yaws);
    const medianPitch = median(pitches);
    const yawMad = medianAbsoluteDeviation(yaws, medianYaw);
    const pitchMad = medianAbsoluteDeviation(pitches, medianPitch);
    const scatter = Math.max(yawMad, pitchMad);
    const stability = Math.max(0, 1 - scatter / this.stabilityReference);
    const meanConfidence =
      inWindow.reduce((total, sample) => total + sample.confidence, 0) / inWindow.length;

    return {
      pose: headPose(medianYaw, medianPitch, median(rolls)),
      sampleCount: inWindow.length,
      windowSpanSeconds: (inWindow[inWindow.length - 1] as Sample).ts - (inWindow[0] as Sample).ts,
      meanConfidence,
      yawMad,
      pitchMad,
      stability,
      // Geometric mean: a head that is moving should reduce certainty, not erase a
      // measurement that is otherwise sound.
      effectiveConfidence: combineConfidence(meanConfidence, stability),
    };
  }

  reset(): void {
    this.samples = [];
  }

  get sampleCount(): number {
    return this.samples.length;
  }
}

export function median(values: readonly number[]): number {
  if (values.length === 0) throw new RangeError('median of an empty list');
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? (sorted[middle] as number)
    : ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2;
}

function medianAbsoluteDeviation(values: readonly number[], centre: number): number {
  if (values.length < 2) return 0;
  return median(values.map((value) => Math.abs(value - centre)));
}
